

import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, Button, FormControl, FormLabel,
  Spinner, Center, Text, Input, List, ListItem, Box,
} from "@chakra-ui/react";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "Configuration/apiConfig";

export default function UpdateActivationModal({ isOpen, onClose, detail, onSave, isUpdating }) {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoadingProducts(true);
      setError("");
      try {
        const res = await apiClient.get(API_CONFIG.ENDPOINTS.STORE_REPORT.ACTIVATION.PRODUCTS);
        setProducts(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error →", err);
        setError("Failed to load products.");
      } finally {
        setIsLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [isOpen]);

  // Pre-fill when detail changes
  useEffect(() => {
    if (detail && products.length > 0) {
      const match = products.find(p => p.productName === detail.product?.productName);
      setSelectedProduct(match || null);
      setSearchText(match?.productName || "");
    }
  }, [detail, products]);

  const filteredProducts = products.filter((p) =>
    p.productName.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setSearchText(product.productName);
    setShowDropdown(false);
  };

  const handleSave = () => onSave?.(detail, {
    productId: selectedProduct?.productId,
    productName: selectedProduct?.productName,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Update Activation</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {isLoadingProducts ? (
            <Center h="100px"><Spinner color="blue.500" /></Center>
          ) : error ? (
            <Center h="100px"><Text color="red.500">{error}</Text></Center>
          ) : (
            <FormControl>
              <FormLabel fontWeight="semibold">Product</FormLabel>
              <Box position="relative">
                <Input
                  placeholder="Search product..."
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setShowDropdown(true);
                    setSelectedProduct(null);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                  focusBorderColor="blue.400"
                  autoComplete="off"
                />
                {showDropdown && filteredProducts.length > 0 && (
                  <List
                    position="absolute" zIndex={999} bg="white" w="100%"
                    border="1px solid" borderColor="gray.200"
                    borderRadius="md" boxShadow="md"
                    maxH="180px" overflowY="auto" mt={1}
                  >
                    {filteredProducts.map((p) => (
                      <ListItem
                        key={p.productId}
                        px={3} py={2}
                        cursor="pointer"
                        _hover={{ bg: "blue.50" }}
                        onMouseDown={() => handleSelectProduct(p)}
                      >
                        {p.productName}
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </FormControl>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          <Button onClick={onClose} variant="ghost">Cancel</Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isUpdating}
            loadingText="Saving..."
            isDisabled={!selectedProduct}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}